/**
 * closing.js — Firestore for the Closing walk-through checklist.
 *
 * Three-level tree (Section → Sub-section → Items):
 *   opsClosingSections/{id}     { title, icon, target, order }
 *   opsClosingSubsections/{id}  { sectionId, title, target, order }
 *   opsClosingItems/{id}        { subsectionId, name, target?, note?, order, keepList? }
 *
 * `target` is a Today-board category id; it flows down (item → sub-section → section).
 * Checking an item is NOT stored here — the checkbox reflects the live Today board
 * (dailyRestockItems), so this collection is purely the editable template.
 */
import {
  collection, doc, query, limit, onSnapshot,
  addDoc, updateDoc, deleteDoc, writeBatch, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { shareFeed } from './liveFeed';
import { rowTracker } from './rows';
import { debugLog, snapInfo } from './debug';
import { ITEMS_COLLECTION, CLOSING_SECTIONS_COLLECTION, CLOSING_SUBSECTIONS_COLLECTION, CLOSING_ITEMS_COLLECTION } from '../config/constants';

const col = name => collection(db, name);

// ── Live subscriptions (sorted client-side to avoid composite indexes) ───────
// Shared + lingering (data/liveFeed.js), so reopening the Closing tab doesn't re-read
// the whole template after a quiet spell.
const sub = (name, onData, onError, cap) => shareFeed(name, (emit, fail) => {
  const rowsOf = rowTracker();
  return onSnapshot(query(col(name), limit(cap)), snap => {
    debugLog('snapshot', name, snapInfo(snap));
    emit(rowsOf(snap));
  }, fail);
}, onData, onError);
export const subscribeClosingSections = (onData, onError) => sub(CLOSING_SECTIONS_COLLECTION, onData, onError, 100);
export const subscribeClosingSubsections = (onData, onError) => sub(CLOSING_SUBSECTIONS_COLLECTION, onData, onError, 500);
export const subscribeClosingItems = (onData, onError) => sub(CLOSING_ITEMS_COLLECTION, onData, onError, 2000);

// ── Sections ─────────────────────────────────────────────────────────────────
export function addClosingSection({ title, icon = '📋', target = 'cooking', order = 0, by }) {
  return addDoc(col(CLOSING_SECTIONS_COLLECTION), { title, icon, target, order, addedBy: by || '', createdAt: serverTimestamp() });
}
export function updateClosingSection(id, patch) {
  return updateDoc(doc(db, CLOSING_SECTIONS_COLLECTION, id), patch);
}
/** Delete a section AND all its sub-sections AND all their items, atomically. */
export function removeClosingSection(id, subIds = [], itemIds = []) {
  const batch = writeBatch(db);
  batch.delete(doc(db, CLOSING_SECTIONS_COLLECTION, id));
  subIds.forEach(sid => batch.delete(doc(db, CLOSING_SUBSECTIONS_COLLECTION, sid)));
  itemIds.forEach(iid => batch.delete(doc(db, CLOSING_ITEMS_COLLECTION, iid)));
  return batch.commit();
}

// ── Sub-sections ─────────────────────────────────────────────────────────────
export function addClosingSubsection({ sectionId, title, target = null, order = 0, by }) {
  return addDoc(col(CLOSING_SUBSECTIONS_COLLECTION), { sectionId, title, target, order, addedBy: by || '', createdAt: serverTimestamp() });
}
export function updateClosingSubsection(id, patch) {
  return updateDoc(doc(db, CLOSING_SUBSECTIONS_COLLECTION, id), patch);
}
/** Delete a sub-section AND all its items, atomically. */
export function removeClosingSubsection(id, itemIds = []) {
  const batch = writeBatch(db);
  batch.delete(doc(db, CLOSING_SUBSECTIONS_COLLECTION, id));
  itemIds.forEach(iid => batch.delete(doc(db, CLOSING_ITEMS_COLLECTION, iid)));
  return batch.commit();
}

// ── Items ────────────────────────────────────────────────────────────────────
export function addClosingItem({ subsectionId, name, target = null, note = '', order = 0, by }) {
  return addDoc(col(CLOSING_ITEMS_COLLECTION), { subsectionId, name, target, note, order, addedBy: by || '', createdAt: serverTimestamp() });
}
export function updateClosingItem(id, patch) {
  return updateDoc(doc(db, CLOSING_ITEMS_COLLECTION, id), patch);
}
export function removeClosingItem(id) {
  return deleteDoc(doc(db, CLOSING_ITEMS_COLLECTION, id));
}
/** Delete many items at once (multi-select delete), atomically. */
export function removeClosingItems(ids = []) {
  const batch = writeBatch(db);
  ids.forEach(id => batch.delete(doc(db, CLOSING_ITEMS_COLLECTION, id)));
  return batch.commit();
}
/** Undo for a multi-delete: re-create the exact docs (same ids, same fields). */
export function restoreClosingItems(rows = []) {
  const batch = writeBatch(db);
  rows.forEach(({ id, ...data }) => batch.set(doc(db, CLOSING_ITEMS_COLLECTION, id), data));
  return batch.commit();
}

/**
 * Change which Today list sections / sub-sections / items feed (or which sub-section items
 * sit in), and move today's board rows that came from them, in one atomic batch (also used
 * for undo). Used by every list dropdown, "Sort into lists" and the multi-select "Move to…".
 *   sections/subsections: [{ id, target }]
 *   items: [{ id, target?, keepList?, subsectionId?, order? }]
 *                                        — `keepList` = the user chose this list by hand,
 *                                          so the name-based suggestion leaves it alone;
 *                                          `subsectionId` + `order` = moved to another sub-section
 *   board: [{ id, category }]            — dailyRestockItems rows to re-file
 */
export function applyClosingTargets({ sections = [], subsections = [], items = [], board = [] }) {
  const batch = writeBatch(db);
  sections.forEach(({ id, target }) => batch.update(doc(db, CLOSING_SECTIONS_COLLECTION, id), { target }));
  subsections.forEach(({ id, target }) => batch.update(doc(db, CLOSING_SUBSECTIONS_COLLECTION, id), { target }));
  items.forEach(({ id, ...patch }) => batch.update(doc(db, CLOSING_ITEMS_COLLECTION, id), patch));
  board.forEach(({ id, category }) => batch.update(doc(db, ITEMS_COLLECTION, id), { category }));
  return batch.commit();
}

/** Persist a new order for two swapped rows (sections, sub-sections, or items). */
export function swapOrder(collectionName, a, b) {
  const batch = writeBatch(db);
  batch.update(doc(db, collectionName, a.id), { order: b.order });
  batch.update(doc(db, collectionName, b.id), { order: a.order });
  return batch.commit();
}

// Queue the whole nested seed (section → sub-section → items) into a batch.
function writeSeed(batch, seed, by) {
  const stamp = () => ({ addedBy: by || '', createdAt: serverTimestamp() });
  seed.forEach((sec, si) => {
    const secRef = doc(col(CLOSING_SECTIONS_COLLECTION));
    batch.set(secRef, { title: sec.title, icon: sec.icon || '📋', target: sec.target || 'cooking', order: (si + 1) * 10, ...stamp() });
    (sec.subs || []).forEach((sub2, sj) => {
      const subRef = doc(col(CLOSING_SUBSECTIONS_COLLECTION));
      batch.set(subRef, { sectionId: secRef.id, title: sub2.title, target: sub2.target || null, order: (sj + 1) * 10, ...stamp() });
      (sub2.items || []).forEach((raw, ii) => {
        const it = typeof raw === 'string' ? { name: raw } : raw;
        batch.set(doc(col(CLOSING_ITEMS_COLLECTION)), {
          subsectionId: subRef.id, name: it.name, target: it.target || null, note: it.note || '',
          order: (ii + 1) * 10, ...stamp(),
        });
      });
    });
  });
}

/**
 * Replace the whole template with the seed, atomically: delete every existing
 * section / sub-section / item, then write the fresh tree in one batch (≈166 writes
 * max, well under Firestore's 500 limit). Used by the "Load / Reset" action.
 */
export function resetClosing(seed, { sectionIds = [], subsectionIds = [], itemIds = [] } = {}, by) {
  const batch = writeBatch(db);
  sectionIds.forEach(id => batch.delete(doc(db, CLOSING_SECTIONS_COLLECTION, id)));
  subsectionIds.forEach(id => batch.delete(doc(db, CLOSING_SUBSECTIONS_COLLECTION, id)));
  itemIds.forEach(id => batch.delete(doc(db, CLOSING_ITEMS_COLLECTION, id)));
  writeSeed(batch, seed, by);
  return batch.commit();
}
